import { useEffect, useState } from "react"
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useRates } from "../../hooks/useRates";
import { CurrentSellerRates, FullSellerRatesHistory, SellerRates, SellerRatesHistoryPaginatorDataContent } from "../../models/Rates";
import { formatCurrency, formatCurrencyToApi, showToast } from "../../utils";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { formatPaymentRangeFromApi, formatPaymentRangeToApi } from "../../utils/formatPaymentRangeToApi";
import { formatPercentageToNumber } from "../../utils/formatPercentageToNumber";
import { useAuth } from "../../contexts/AuthContext";

const editGeneralInformationSchema = yup.object().shape({
  pixDelayForRedeem: yup.string()
    .required("Preenchimento obrigatório!")
    .test("pixDelayForRedeem", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  pixRate: yup.string()
    .required("Preenchimento obrigatório!")
    .test("pixRate", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  pixRateLimitValue: yup.string()
    .required("Preenchimento obrigatório!")
    .test("pixRateLimitValue", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  bankSlipDelayForRedeem: yup.string()
    .required("Preenchimento obrigatório!")
    .test("bankSlipDelayForRedeem", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  bankSlipValue: yup.string()
    .required("Preenchimento obrigatório!")
    .test("bankSlipValue", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  sightCreditCardDelayForRedeem: yup.string()
    .required("Preenchimento obrigatório!")
    .test("sightCreditCardDelayForRedeem", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  sightCreditCardRate: yup.string()
    .required("Preenchimento obrigatório!")
    .test("sightCreditCardRate", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
  earlyCreditCardDelayForRedeem: yup.string()
    .required("Preenchimento obrigatório!")
    .test("earlyCreditCardDelayForRedeem", "Preencimento obrigatório!", (value) => value?.replace(/\D/g, "") === "" ? false : true),
    observation: yup.string()
    .required("Preenchimento obrigatório!")
    .test("observation", "Preencimento obrigatório!", (value) => value === "" ? false : true),
  earlyCreditCardBuyerRates: yup.array()
    .required("Preencimento obrigatório!")
    .test("earlyCreditCardBuyerRates", "Preencimento obrigatório!", (value) => 
    value?.map(item => item?.replace(/\D/g, "") === "" ? false : true).find(item => item === false) === undefined ? true : false),
  earlyCreditCardSellerRates: yup.array()
    .required("Preencimento obrigatório!")
    .test("earlyCreditCardSellerRates", "Preencimento obrigatório!", (value) => 
    value?.map(item => item?.replace(/\D/g, "") === "" ? false : true).find(item => item === false) === undefined ? true : false),
});

interface FormValues {
  pixDelayForRedeem: string;
  pixRate: string;
  pixRateLimitValue: string;
  bankSlipDelayForRedeem: string;
  bankSlipValue: string;
  debitValue: string;
  debitDelayForRedeem: string;
  sightCreditCardDelayForRedeem: string;
  sightCreditCardRate: string;
  earlyCreditCardDelayForRedeem: string;
  earlyCreditCardBuyerRates: Array<string>;
  earlyCreditCardSellerRates: Array<string>;
  observation: string;
}

export function useTransactionalCosts() {
  const [currentSellerRates, setCurrentSellerRates] = useState<CurrentSellerRates>({} as CurrentSellerRates);
  const [defaultSellerRates, setDefaultSellerRates] = useState<CurrentSellerRates>({} as CurrentSellerRates);
  const [sellerRatesHistory, setSellerRatesHistory] = useState<SellerRatesHistoryPaginatorDataContent>({} as SellerRatesHistoryPaginatorDataContent);
  const [confirmationModalState, setConfirmationModalState] = useState<boolean>(false);
  const [allValuesAreDefault, setAllValuesAreDefault] = useState<boolean>(false);
  const [allValuesAreEqualToCurrentSellerRates, setAllValuesAreEqualToCurrentSellerRates] = useState<boolean>(false);
  const [sellerRatesHistoryCurrentPage ] = useState<number>(0);
  const [sellerRatesHistoryAmountPerPage, setSellerRatesHistoryAmountPerPage] = useState<number>(3);
  const [fullHistoryResponse, setFullHistoryResponse] = useState<FullSellerRatesHistory>({} as FullSellerRatesHistory);
  const [allBuyerRatesAreDefault, setAllBuyerRatesAreDefault] = useState<boolean>(false);
  const [allSellerRatesAreDefault, setAllSellerRatesAreDefault] = useState<boolean>(false);
  const { patchSellerRates, getFullSellerRates, getFullSellerRatesHistory, getSellerRatesHistory } = useRates();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    control,
    getValues,
    setError,
    clearErrors
  } = useForm({
    resolver: yupResolver(editGeneralInformationSchema)
  });

  const { setEditionModeState } = useAuth();

  const { id } = useParams();

  async function getInitialData() {
    try {
      const sellerRatesResponse = await getFullSellerRates(id as string);
      setCurrentSellerRates(sellerRatesResponse?.data?.currentSellerRatesConfig);
      setDefaultSellerRates(sellerRatesResponse?.data?.defaultRatesConfig);
      setInitialValues(sellerRatesResponse?.data?.currentSellerRatesConfig);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    }
  }

  async function handleGetSellerRatesHistory() {
    try {
      const sellerRatesHistoryResponse = await getSellerRatesHistory(
        id as string,
        { startDate: "2022-08-24" },
        { size: sellerRatesHistoryAmountPerPage, page: sellerRatesHistoryCurrentPage, sort: "createdAt,DESC" }
      );
      setSellerRatesHistory(sellerRatesHistoryResponse?.data);
      setSellerRatesHistoryAmountPerPage(sellerRatesHistoryAmountPerPage + 3);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    }
  }

  async function handleGetFullSellerRatesHistory(id: string) {
    try {
      const { data } = await getFullSellerRatesHistory(id);
      setFullHistoryResponse(data);

    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    }
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const transactionalCostsDTO: SellerRates = formatValues(getValues() as FormValues);
      handlePatchApiCall(transactionalCostsDTO);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    }
  };

  async function handlePatchApiCall(values: SellerRates) {
    let addValuesMostUsed = {...values}
    addValuesMostUsed.currentSellerRatesRequest.earlyCreditCardConfigData.mostUsed = defaultSellerRates?.earlyCreditCardConfigData.mostUsed
    try {
      await patchSellerRates(id as string, values);
      setConfirmationModalState(false);
      setEditionModeState(false);
      showToast({
        type: "success",
        message: "Custos transacionais alterados com sucesso"
      });
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    }
  }

  function verifyAllValuesAreCorrect(flag: boolean) {
    const allValues = formatValues(getValues() as FormValues);
    return allValuesAreCorrect(allValues, flag);
  }

  function undoChanges() {
    setValue("pixDelayForRedeem", formatPaymentRangeFromApi(currentSellerRates?.pixConfigData?.delayForRedeem));
    setValue("pixRate", formatPercentageToNumber(currentSellerRates?.pixConfigData?.rate) as string);
    setValue("pixRateLimitValue", formatCurrency({ value: currentSellerRates?.pixConfigData?.rateLimitValue }) as string);
    setValue("bankSlipDelayForRedeem", formatPaymentRangeFromApi(currentSellerRates?.bankSlipConfigData?.delayForRedeem));
    setValue("rateType", currentSellerRates?.bankSlipConfigData?.rateType ?? "value");
    setValue("bankSlipValue", currentSellerRates?.bankSlipConfigData?.rateType === "percent" ? formatPercentageToNumber(currentSellerRates?.bankSlipConfigData?.rate) : formatCurrency({ value: currentSellerRates?.bankSlipConfigData?.value }) as string);
    setValue("rateLimitValue", formatCurrency({ value:currentSellerRates?.bankSlipConfigData?.rateLimitValue }));

    if (currentSellerRates?.bankSlipConfigData?.rateLimitValue) {
      setValue("rateLimitValue", formatCurrency({ value: currentSellerRates?.bankSlipConfigData?.rateLimitValue }));
    } else {
      setValue("rateLimitValue",formatCurrency({ value: 100000 }))
    }

    setValue("sightCreditCardDelayForRedeem", formatPaymentRangeFromApi(currentSellerRates?.sightCreditCardConfigData?.delayForRedeem));
    setValue("sightCreditCardRate", formatPercentageToNumber(currentSellerRates?.sightCreditCardConfigData?.rate) as string);
    setValue("earlyCreditCardDelayForRedeem", formatPaymentRangeFromApi(currentSellerRates?.earlyCreditCardConfigData?.delayForRedeem));
    setValue("earlyCreditCardBuyerRates", currentSellerRates?.earlyCreditCardConfigData?.buyerRates.map(item => formatPercentageToNumber(item)));
    setValue("earlyCreditCardSellerRates", currentSellerRates?.earlyCreditCardConfigData?.sellerRates.map(item => formatPercentageToNumber(item)));
  }

  async function setValuesToDefault() {
    setValue("pixDelayForRedeem", formatPaymentRangeFromApi(defaultSellerRates?.pixConfigData?.delayForRedeem));
    setValue("pixRate", formatPercentageToNumber(defaultSellerRates?.pixConfigData?.rate) as string);
    setValue("pixRateLimitValue", formatCurrency({ value: defaultSellerRates?.pixConfigData?.rateLimitValue }) as string);
    setValue("bankSlipDelayForRedeem", formatPaymentRangeFromApi(defaultSellerRates?.bankSlipConfigData?.delayForRedeem));
    setValue("rateType", defaultSellerRates?.bankSlipConfigData?.rateType ?? "value");
    setValue("bankSlipValue", defaultSellerRates?.bankSlipConfigData?.rateType === "percent" ? formatPercentageToNumber(defaultSellerRates?.bankSlipConfigData?.rate) : formatCurrency({ value: defaultSellerRates?.bankSlipConfigData?.value }) as string);
    
    if (defaultSellerRates?.bankSlipConfigData?.rateLimitValue) {
      setValue("rateLimitValue", formatCurrency({ value: defaultSellerRates?.bankSlipConfigData?.rateLimitValue }));
    } else {
      setValue("rateLimitValue",formatCurrency({ value: 100000 }))
    }

    setValue("sightCreditCardDelayForRedeem", formatPaymentRangeFromApi(defaultSellerRates?.sightCreditCardConfigData?.delayForRedeem));
    setValue("sightCreditCardRate", formatPercentageToNumber(defaultSellerRates?.sightCreditCardConfigData?.rate) as string);
    setValue("earlyCreditCardDelayForRedeem", formatPaymentRangeFromApi(defaultSellerRates?.earlyCreditCardConfigData?.delayForRedeem));
    setValue("earlyCreditCardBuyerRates", defaultSellerRates?.earlyCreditCardConfigData?.buyerRates.map(item => formatPercentageToNumber(item)));
    setValue("earlyCreditCardSellerRates", defaultSellerRates?.earlyCreditCardConfigData?.sellerRates.map(item => formatPercentageToNumber(item)));
  }

  async function setInitialValues(data: CurrentSellerRates) {
    setValue("pixDelayForRedeem", formatPaymentRangeFromApi(data?.pixConfigData?.delayForRedeem));
    setValue("pixRate", formatPercentageToNumber(data?.pixConfigData?.rate) as string);
    setValue("pixRateLimitValue", formatCurrency({ value: data?.pixConfigData?.rateLimitValue }) as string);
    setValue("bankSlipDelayForRedeem", formatPaymentRangeFromApi(data?.bankSlipConfigData?.delayForRedeem));
    setValue("debitDelayForRedeem", formatPaymentRangeFromApi(data?.debitCardConfigData?.delayForRedeem))
    setValue("debitValue", data?.debitCardConfigData?.rateType === "percent" ? formatPercentageToNumber(data?.debitCardConfigData?.rate) : formatCurrency({ value: data?.debitCardConfigData?.rate }) as string);
    setValue("rateType", data?.bankSlipConfigData?.rateType === undefined ? defaultSellerRates?.bankSlipConfigData?.rateType : data?.bankSlipConfigData?.rateType);
    
    if (data?.bankSlipConfigData?.rateLimitValue) {
      setValue("rateLimitValue", formatCurrency({ value: data?.bankSlipConfigData?.rateLimitValue }));
    } else {
      setValue("rateLimitValue",formatCurrency({ value: 100000 }))
    }
    
    setValue("bankSlipValue", data?.bankSlipConfigData?.rateType === "percent" ? formatPercentageToNumber(data?.bankSlipConfigData?.rate) : formatCurrency({ value: data?.bankSlipConfigData?.value }) as string);
    setValue("sightCreditCardDelayForRedeem", formatPaymentRangeFromApi(data?.sightCreditCardConfigData?.delayForRedeem));
    setValue("sightCreditCardRate", formatPercentageToNumber(data?.sightCreditCardConfigData?.rate) as string);
    setValue("earlyCreditCardDelayForRedeem", formatPaymentRangeFromApi(data?.earlyCreditCardConfigData?.delayForRedeem));
    setValue("earlyCreditCardBuyerRates", data?.earlyCreditCardConfigData?.buyerRates.map(item => formatPercentageToNumber(item)));
    setValue("earlyCreditCardSellerRates", data?.earlyCreditCardConfigData?.sellerRates.map(item => formatPercentageToNumber(item)));
  }

  function formatValues(baseValues?: FormValues): SellerRates {
    let values = getValues();
    if (baseValues) {
      values = baseValues;
    }
    return {
      currentSellerRatesRequest: {
        pixConfigData: {
          delayForRedeem: formatPaymentRangeToApi(values?.pixDelayForRedeem) as number,
          rate: formatPercentageToNumber(values?.pixRate, true) as number,
          rateLimitValue: formatCurrencyToApi(values?.pixRateLimitValue) as number
        },
        debitCardConfigData: {
          delayForRedeem: formatPaymentRangeToApi(values?.debitDelayForRedeem) as number,
          rate: values?.rateType === "percent" ? formatPercentageToNumber(values?.debitValue, true) as number : formatCurrencyToApi(values?.debitValue) as number,
          rateType: values?.rateType as string,
        },
        bankSlipConfigData: {
          delayForRedeem: formatPaymentRangeToApi(values?.bankSlipDelayForRedeem) as number,
          value: values?.rateType === "percent" ? formatPercentageToNumber(values?.bankSlipValue, true) as number : formatCurrencyToApi(values?.bankSlipValue) as number,
          rate: values?.rateType === "percent" ? formatPercentageToNumber(values?.bankSlipValue, true) as number : formatCurrencyToApi(values?.bankSlipValue) as number,
          rateType: values?.rateType as string,
          rateLimitValue:  formatCurrencyToApi(values?.rateLimitValue) as number
        },
        sightCreditCardConfigData: {
          delayForRedeem: formatPaymentRangeToApi(values?.sightCreditCardDelayForRedeem) as number,
          rate: formatPercentageToNumber(values?.sightCreditCardRate, true) as number
        },
        earlyCreditCardConfigData: {
          delayForRedeem: formatPaymentRangeToApi(values?.earlyCreditCardDelayForRedeem) as number,
          buyerRates: values?.earlyCreditCardBuyerRates?.map(item => formatPercentageToNumber(item, true)) as Array<number>,
          sellerRates: values?.earlyCreditCardSellerRates?.map(item => formatPercentageToNumber(item, true)) as Array<number>
        }
      },
      observation: values?.observation,
      isDefault: allValuesAreDefault
    }
  }

  function areAllValuesEqualToCurrentSellerRates(baseValues: SellerRates) {
    if (!baseValues?.currentSellerRatesRequest?.bankSlipConfigData?.delayForRedeem) return;
    const allValuesEqualToCurrentSellerRates = Object?.keys(baseValues?.currentSellerRatesRequest)?.map(externalObject => {
      return Object.keys(currentSellerRates[externalObject])?.map(innerObject => {
        if (innerObject === "buyerRates" || innerObject === "sellerRates") {
          return baseValues?.currentSellerRatesRequest[externalObject][innerObject].map((item, index) => {
            return item === currentSellerRates[externalObject][innerObject].at(index);
          }).find(item => item === false);
        } else {
          return baseValues?.currentSellerRatesRequest[externalObject][innerObject] === currentSellerRates[externalObject][innerObject];
        }
      }).find(item => item === false);
    }).find(item => item === false);
    setAllValuesAreEqualToCurrentSellerRates(allValuesEqualToCurrentSellerRates === undefined ? true : false);
    return allValuesEqualToCurrentSellerRates === undefined ? true : false;
  }

  function areAllValuesDefault(baseValues: SellerRates) {
    if (!baseValues?.currentSellerRatesRequest?.bankSlipConfigData?.delayForRedeem) return;
    const allValuesAreTheSameAsDefault = Object?.keys(baseValues?.currentSellerRatesRequest)?.map(externalObject => {
      return Object.keys(currentSellerRates[externalObject])?.map(innerObject => {
        if (innerObject === "buyerRates" || innerObject === "sellerRates") {
          return baseValues?.currentSellerRatesRequest[externalObject][innerObject].map((item, index) => {
            return item !== defaultSellerRates[externalObject][innerObject].at(index) ? false : undefined
          }).find(item => item === false);
        } else {
          return baseValues?.currentSellerRatesRequest[externalObject][innerObject] !== defaultSellerRates[externalObject][innerObject] ? false : undefined
        }
      }).find(item => item === false);
    }).find(item => item === false);
    setAllValuesAreDefault(allValuesAreTheSameAsDefault === undefined ? true : false);
    return allValuesAreTheSameAsDefault === undefined ? true : false;
  }

  function allValuesAreCorrect(baseValues: SellerRates, flag: boolean) {
    const innerObjectCorrectName = {
      pixConfigData: {
        "delayForRedeem": "pixDelayForRedeem",
        "rate": "pixRate",
        "rateLimitValue": "pixRateLimitValue",
      },
      bankSlipConfigData: {
        "delayForRedeem": "bankSlipDelayForRedeem",
        "value": "bankSlipValue",
        "rateType": "rateType",
        "rate": "bankSlipValue",
        "rateLimitValue": "rateLimitValue"
      },
      debitCardConfigData: {
        "delayForRedeem": "debitDelayForRedeem",
        "rateType": "rateType",
        "rate": "debitValue",
      },
      sightCreditCardConfigData: {
        "delayForRedeem": "sightCreditCardDelayForRedeem",
        "rate": "sightCreditCardRate"
      },
      earlyCreditCardConfigData: {
        "delayForRedeem": "earlyCreditCardDelayForRedeem"
      }
    }
    const hasErrors = Object.keys(baseValues?.currentSellerRatesRequest)?.map(currentSellerRatesInnerObject => {
      return Object.keys(baseValues?.currentSellerRatesRequest[currentSellerRatesInnerObject])?.map(innerObject => {
        if (innerObject === "buyerRates" || innerObject === "sellerRates") {
          return baseValues?.currentSellerRatesRequest[currentSellerRatesInnerObject][innerObject]?.map((item: number, index: number) => {
            const objectName = innerObject === "buyerRates" ? 
                  "earlyCreditCardBuyerRates" : 
                  "earlyCreditCardSellerRates";
            const gotAnError = !(item >= 0);
            if (gotAnError) {
              setError(`${objectName}.${index}`, {type: "manual", message:"Preenchimento obrigatório!"}, {shouldFocus: true});
            } 
            if (!gotAnError) {
              clearErrors(`${objectName}.${index}`);
            }
            return gotAnError;
          });
        } else {
          const gotAnError = innerObject !== "delayForRedeem" ? 
                !(baseValues?.currentSellerRatesRequest[currentSellerRatesInnerObject][innerObject] > 0) : 
                !(baseValues?.currentSellerRatesRequest[currentSellerRatesInnerObject][innerObject] >= 0);
          if (innerObject === "rateType") {
            return false;
          }
          if (gotAnError) {
            setError(innerObjectCorrectName[currentSellerRatesInnerObject][innerObject], {type: "manual", message:"Preenchimento obrigatório!"}, {shouldFocus: true});
          } 
          if (!gotAnError) {
            clearErrors(innerObjectCorrectName[currentSellerRatesInnerObject][innerObject]);
          }
          return gotAnError;
        }
      })
    }).map(item => {
      return item.map(innerItem => {
        if (innerItem.length) {
          return innerItem.map(item => item === true).at(0)
        } else {
          return innerItem === true ? true : false;
        }
      }).find(itin => itin === true);
    }).find(iton => iton === true);
    if (hasErrors) {
      return true;
    }
    const gotAnError = !(baseValues.observation.length > 0);
    if (gotAnError) {
      setError("observation", {type: "manual", message:"Preenchimento obrigatório!"}, {shouldFocus: flag});
    }
    if (!gotAnError) {
      clearErrors("observation");
    }
    if (hasErrors) {
      return true;
    } else if (gotAnError){
      return true;
    }
    return false;
  }

  function allBuyerTaxesAreDefault(baseValues: SellerRates) {
    if (!baseValues?.currentSellerRatesRequest?.bankSlipConfigData?.delayForRedeem) return;
    const result = baseValues.currentSellerRatesRequest.earlyCreditCardConfigData.buyerRates.map((item, index) => {
      return (item === defaultSellerRates.earlyCreditCardConfigData.buyerRates.at(index))
    }).find(item => item === false);
    setAllBuyerRatesAreDefault(result === undefined ? true : false);
  }

  function allSellerTaxesAreDefault(baseValues: SellerRates) {
    if (!baseValues?.currentSellerRatesRequest?.bankSlipConfigData?.delayForRedeem) return;
    const result = baseValues.currentSellerRatesRequest.earlyCreditCardConfigData.sellerRates.map((item, index) => {
      return (item === defaultSellerRates.earlyCreditCardConfigData.sellerRates.at(index))
    }).find(item => item === false);
    setAllSellerRatesAreDefault(result === undefined ? true : false);
  }

  function colorFinder(actualValue?: number | string, defaultValue?: number) {
    return actualValue === defaultValue ? "#000" : "var(--gold-700)";
  }

  function returnDefaultValue(name: string, objectName: string) {
    return defaultSellerRates[objectName][name];
  }

  function hasReturnValue(defaultValue?: number, actualValue?: number) {
    return defaultValue === actualValue;
  }

  function handleValue(name: string, data: any) {
    setValue(name as any, data);
  }

  useEffect(() => {
    getInitialData();
    handleGetSellerRatesHistory();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const result = formatValues(getValues() as FormValues);
    areAllValuesEqualToCurrentSellerRates(result);
    areAllValuesDefault(result);
    allBuyerTaxesAreDefault(result);
    allSellerTaxesAreDefault(result);
    // eslint-disable-next-line
  }, [getValues()]);

  return {
    currentSellerRates,
    defaultSellerRates,
    colorFinder,
    returnDefaultValue,
    hasReturnValue,
    setCurrentSellerRates,
    onSubmit,
    handleSubmit,
    register,
    handleValue,
    watch,
    errors,
    setValue,
    control,
    setValuesToDefault,
    undoChanges,
    confirmationModalState,
    setConfirmationModalState,
    allValuesAreDefault,
    sellerRatesHistory,
    handleGetSellerRatesHistory,
    allValuesAreEqualToCurrentSellerRates,
    allBuyerRatesAreDefault,
    allSellerRatesAreDefault,
    handleGetFullSellerRatesHistory,
    fullHistoryResponse,
    setError,
    verifyAllValuesAreCorrect
  }
}